/**
 * apps/automations-portal/src/features/auth/guards.tsx
 *
 * Role resolution and route guards.
 *
 * There is no dedicated "who am I" endpoint on the backend, so role is
 * inferred from which authenticated route answers successfully:
 *
 *   1. GET /api/client/me       — succeeds            => role 'client'
 *   2. on 403 NO_PORTAL_ACCESS, GET /api/admin/blocks  — succeeds => role 'admin'
 *   3. otherwise                                       => role 'none'
 *
 * Any other failure from /api/client/me (network error, 500, 501 — the
 * route may not exist yet) is treated as 'none' rather than guessed at,
 * since the specific 403/NO_PORTAL_ACCESS signal is the only documented
 * "you're signed in but this isn't your account type" contract. The result
 * is cached in context for the session (recomputed only when the signed-in
 * user changes) so navigating between pages doesn't re-probe both routes
 * every time.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { adminApi, clientApi, ApiError } from '@/lib/api';
import { useAuth } from './AuthProvider';

export type AccessRole = 'admin' | 'client' | 'none' | 'loading';

interface AccessContextValue {
  role: AccessRole;
  refresh: () => void;
}

const AccessContext = createContext<AccessContextValue | null>(null);

export function AccessProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [role, setRole] = useState<AccessRole>('loading');
  const [generation, setGeneration] = useState(0);

  const resolve = useCallback(async () => {
    if (!user) {
      setRole('none');
      return;
    }
    setRole('loading');
    try {
      await clientApi.me();
      setRole('client');
    } catch (err) {
      if (err instanceof ApiError && err.status === 403 && err.code === 'NO_PORTAL_ACCESS') {
        try {
          await adminApi.listBlocks();
          setRole('admin');
        } catch {
          setRole('none');
        }
        return;
      }
      setRole('none');
    }
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    void resolve();
  }, [authLoading, resolve, generation]);

  const refresh = useCallback(() => setGeneration((g) => g + 1), []);

  return <AccessContext.Provider value={{ role, refresh }}>{children}</AccessContext.Provider>;
}

export function useAccess(): AccessContextValue {
  const ctx = useContext(AccessContext);
  if (!ctx) throw new Error('useAccess must be used inside AccessProvider');
  return ctx;
}

function FullScreenMessage({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="max-w-md text-center">
        <h1 className="text-lg font-semibold text-foreground">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{body}</p>
      </div>
    </div>
  );
}

export function NoAccessScreen() {
  return (
    <FullScreenMessage
      title="This account doesn't have access"
      body="Your sign-in worked, but this account isn't set up on Hattie Automations yet. If you think that's wrong, get in touch with whoever set up your automation engine."
    />
  );
}

/** Redirects to /sign-in when signed out; shows nothing while auth is still resolving. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;
  if (!user) return <Navigate to="/sign-in" replace state={{ from: location }} />;
  return <>{children}</>;
}

/** Renders children only once role resolves to 'admin'; otherwise renders nothing while loading and the no-access screen once resolved. */
export function AdminOnly({ children }: { children: ReactNode }) {
  const { role } = useAccess();
  if (role === 'loading') return null;
  if (role !== 'admin') return <NoAccessScreen />;
  return <>{children}</>;
}

/** Renders children only once role resolves to 'client'; otherwise renders nothing while loading and the no-access screen once resolved. */
export function ClientOnly({ children }: { children: ReactNode }) {
  const { role } = useAccess();
  if (role === 'loading') return null;
  if (role !== 'client') return <NoAccessScreen />;
  return <>{children}</>;
}
